import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import {
  listWhoWeServeAdmin,
  reorderWhoWeServeItems,
  deleteWhoWeServeItem,
} from "@/lib/api/marketing.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { MarketingAdminNav } from "@/components/admin/MarketingAdminNav";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/marketing/who-we-serve/")({
  loader: () => listWhoWeServeAdmin(),
  component: WhoWeServeListPage,
});

type Row = Awaited<ReturnType<typeof listWhoWeServeAdmin>>[number];

function WhoWeServeListPage() {
  const initial = Route.useLoaderData();
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);

  async function persistOrder(next: Row[]) {
    setBusy(true);
    try {
      await reorderWhoWeServeItems({ data: { ids: next.map((r) => r.id) } });
      setRows(next);
      toast.success("Order updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to reorder");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row: Row) {
    try {
      await deleteWhoWeServeItem({ data: { id: row.id } });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Item deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  const columns: ColumnDef<Row>[] = [
    {
      accessorKey: "name",
      header: "Audience",
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: "body",
      header: "Description",
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-md text-muted-foreground">{row.original.body ?? "—"}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      ...tableActionsColumn<Row>(),
      cell: ({ row }) => (
        <TableRowActions
          edit={{ to: "/admin/marketing/who-we-serve/$id", params: { id: row.original.id } }}
          onDelete={() => handleDelete(row.original)}
          deleteLabel={row.original.name}
        />
      ),
    },
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Marketing"
        action={
          <Button asChild>
            <Link to="/admin/marketing/who-we-serve/new">
              <Plus className="mr-2 h-4 w-4" /> Add audience
            </Link>
          </Button>
        }
      />
      <MarketingAdminNav />
      <p className="mb-4 text-sm text-muted-foreground">
        Shown on the homepage and the Who We Serve page. Drag rows to reorder.
      </p>
      <DataTable
        tableId="who-we-serve"
        columns={columns}
        data={rows}
        enableRowReorder
        onReorder={persistOrder}
        reorderDisabled={busy}
      />
    </AdminPageShell>
  );
}
